import { createRouter, createWebHashHistory, type RouteLocationNormalized, type NavigationGuardNext } from 'vue-router'
import { AppIdentity } from '../ptfm/core/base/identity'
import { SessionStorageHelper } from '../ptfm/core/helpers/sessionstorage/sessionstoragehelper'
import { anonymousRoutes } from './anonymous.router'
import { getThemeLayout, initNavigationHelper } from '../themes/theme-helper'


const routes = [
  {
    path: '/',
    redirect: '/signin'
  },
  ...anonymousRoutes,
  {
    path: '/',
    component: getThemeLayout(),
    meta: { requiresAuth: true },
    children: [
      {
        // Constrain to numeric ids so path-based forms like
        // /form/hrms/employees/create don't collide with this DB-id route.
        path: 'form/:formId(\\d+)/:pkId(\\d+)/:configId(\\d+)',
        name: 'form-view',
        component: () => import('../engines/views/FormRendererView.vue'),
        meta: { requiresAuth: true },
      },
      {
        // File-backed form renderer. URL convention: any path under /form/**
        // resolves to an _apps/** form config (json/html/js). Sits above the
        // generic :path+ catch-all so it matches first.
        path: 'form/:path+',
        name: 'dynamic-form',
        component: () => import('../engines/views/FormRendererView.vue'),
        meta: { requiresAuth: true },
        props: (route: RouteLocationNormalized) => ({
          appPath: Array.isArray(route.params.path)
            ? route.params.path.join('/')
            : route.params.path,
          routeParams: { ...route.query } as Record<string, any>
        })
      },
      {
        path: ':path+',
        name: 'dynamic-app',
        component: () => import('../engines/views/DynamicAppView.vue'),
        meta: { requiresAuth: true },
        props: (route: RouteLocationNormalized) => ({
          appPath: Array.isArray(route.params.path)
            ? route.params.path.join('/')
            : route.params.path,
          routeParams: { ...route.query } as Record<string, any>
        })
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/signin'
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  routes
})

// Navigation guard to check authentication
router.beforeEach((to: RouteLocationNormalized, _: RouteLocationNormalized, next: NavigationGuardNext) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const currentIdentity = AppIdentity.getActiveIdentity()
  
  if (requiresAuth) {
    // First check if identity cookie exists
    if (!currentIdentity) {
      next('/signin')
      return
    }
    
    // Then check if we have an active session token
    if (!SessionStorageHelper.getToken()) {
      next('/signin')
      return
    }

    // Both checks passed, allow access to protected route
    next()
  } else if (to.path === '/signin') {
    // For signin page, redirect to dashboard if already authenticated
    if (currentIdentity && SessionStorageHelper.getToken()) {
      next('/3cap/dashboard')
    } else {
      next()
    }
  } else {
    next()
  }
})

// Initialize the theme's NavigationHelper with the router instance
initNavigationHelper(router)

export default router
